import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Form, InputGroup } from 'react-bootstrap';
import type { ChangeWorkGroup, InternalSubtaskTemplate } from '../../../types/changes';
import { changesApi } from '../../../services/changesApi';
import { getApiErrorMessage } from '../../../utils/apiError';
import { internalSubtaskTemplatePath } from '../catalogRegistry';

interface Props {
  group: ChangeWorkGroup;
  onToggleAuto: () => void | Promise<void>;
  onToggleThread: () => void | Promise<void>;
  onTemplateIdsChange: (templateIds: number[]) => void | Promise<void>;
  onImported?: () => void | Promise<void>;
}

const ChangeWorkGroupInternalChecklistEditor: React.FC<Props> = ({
  group,
  onTemplateIdsChange,
  onImported,
}) => {
  const [templates, setTemplates] = useState<InternalSubtaskTemplate[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [importing, setImporting] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState('');

  const loadTemplates = useCallback(async () => {
    try {
      const data = await changesApi.listInternalSubtaskTemplates({ only_active: true });
      setTemplates(data.items || []);
      setLoadError(null);
    } catch (err: unknown) {
      setLoadError(getApiErrorMessage(err, 'Falha ao carregar subtarefas internas.'));
    }
  }, []);

  useEffect(() => {
    void loadTemplates();
  }, [loadTemplates, group.internal_templates, group.internal_subtask_template_id]);

  const selectedIds = (group.internal_templates || [])
    .map((item) => item.id)
    .concat(
      group.internal_subtask_template_id &&
        !(group.internal_templates || []).some((item) => item.id === group.internal_subtask_template_id)
        ? [group.internal_subtask_template_id]
        : [],
    );
  const showLegacyImport =
    templates.length === 0 && selectedIds.length === 0 && (group.checklist_field_count ?? 0) > 0;

  const persistIds = async (next: number[]) => {
    setSavingTemplate(true);
    try {
      await onTemplateIdsChange(next);
    } finally {
      setSavingTemplate(false);
    }
  };

  const handleToggleTemplate = async (templateId: number, checked: boolean) => {
    const next = checked
      ? selectedIds.includes(templateId)
        ? selectedIds
        : [...selectedIds, templateId]
      : selectedIds.filter((id) => id !== templateId);
    await persistIds(next);
  };

  const handleCreate = async () => {
    const name = newName.trim();
    if (!name) {
      setLoadError('Informe o nome da subtarefa interna.');
      return;
    }
    setCreating(true);
    setLoadError(null);
    try {
      const created = await changesApi.createInternalSubtaskTemplate({
        name: name.slice(0, 255),
        show_on_dispatch: true,
      });
      await loadTemplates();
      await persistIds([...selectedIds, created.id]);
      setShowCreate(false);
      setNewName('');
      await onImported?.();
    } catch (err: unknown) {
      setLoadError(getApiErrorMessage(err, 'Não foi possível criar a subtarefa interna.'));
    } finally {
      setCreating(false);
    }
  };

  const handleImport = async () => {
    setImporting(true);
    setLoadError(null);
    try {
      const created = await changesApi.importInternalSubtaskFromGroup(group.group_id);
      await loadTemplates();
      await persistIds(selectedIds.includes(created.id) ? selectedIds : [...selectedIds, created.id]);
      await onImported?.();
    } catch (err: unknown) {
      setLoadError(getApiErrorMessage(err, 'Não foi possível usar os campos antigos deste grupo.'));
    } finally {
      setImporting(false);
    }
  };

  return (
    <>
      {loadError ? (
        <Alert variant="warning" className="py-2" dismissible onClose={() => setLoadError(null)}>
          {loadError}
        </Alert>
      ) : null}

      {showCreate ? (
        <InputGroup className="mb-3">
          <Form.Control
            id={`new-internal-name-${group.group_id}`}
            aria-label="Nome"
            placeholder="Nome da subtarefa"
            value={newName}
            maxLength={255}
            autoFocus
            disabled={creating}
            onChange={(event) => setNewName(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                void handleCreate();
              }
              if (event.key === 'Escape') {
                setShowCreate(false);
                setNewName('');
              }
            }}
          />
          <Button disabled={!newName.trim() || creating} onClick={() => void handleCreate()}>
            {creating ? 'Criando…' : 'Criar'}
          </Button>
        </InputGroup>
      ) : (
        <div className="d-flex justify-content-end mb-3">
          <Button
            size="sm"
            variant="outline-primary"
            disabled={savingTemplate}
            onClick={() => {
              setShowCreate(true);
              setNewName('');
              setLoadError(null);
            }}
          >
            Nova
          </Button>
        </div>
      )}

      {templates.length === 0 ? (
        <p className="text-muted small mb-3">Nenhuma subtarefa interna.</p>
      ) : (
        <div className="d-flex flex-column gap-2 mb-3">
          {templates.map((tpl) => (
            <div key={tpl.id} className="d-flex align-items-center justify-content-between gap-2">
              <Form.Check
                className="flex-grow-1 mb-0"
                type="checkbox"
                id={`internal-subtask-${group.group_id}-${tpl.id}`}
                label={tpl.name}
                checked={selectedIds.includes(tpl.id)}
                disabled={savingTemplate}
                onChange={(e) => void handleToggleTemplate(tpl.id, e.target.checked)}
              />
              <Button
                size="sm"
                variant="outline-primary"
                href={internalSubtaskTemplatePath(tpl.id)}
                aria-label={`Editar ${tpl.name}`}
              >
                <i className="bi bi-pencil me-1" aria-hidden="true" />
                Editar
              </Button>
            </div>
          ))}
        </div>
      )}

      {showLegacyImport ? (
        <Button
          type="button"
          variant="link"
          size="sm"
          className="px-0 mt-2"
          disabled={importing}
          onClick={() => void handleImport()}
        >
          {importing ? 'Usando…' : 'Usar campos antigos'}
        </Button>
      ) : null}
    </>
  );
};

export default ChangeWorkGroupInternalChecklistEditor;
